import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { LoadingSpinner } from "components/LoadingSpinner";
import { ISPAConfig } from "config/api";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useCreateOrderSetupIntentMutation } from "store/queries/order";
import { useUpdatePaymentMethodMutation } from "store/queries/payment";
import { IOrder } from "types/Order/Order";

import { ContainerForm, StyledInfoContainer } from "../styled";

const getPublishableKey = () => {
  const config = window?.config as ISPAConfig;

  if (!config) {
    throw new Error("Config not found");
  }

  return config.stripe.publishableKey;
};

function CardDetails({
  orderInfo,
  onReturnClick,
  setShowUpdateSuccessFull,
}: {
  orderInfo: any;
  onReturnClick: () => void;
  setShowUpdateSuccessFull: (value: boolean) => void;
}) {
  const [buttonLoading, setButtonLoading] = useState(false);
  const [cardInformationComplete, setCardInformationComplete] = useState(false);
  const elements = useElements();
  const stripe = useStripe();
  const [updatePaymentMethod] = useUpdatePaymentMethodMutation();
  const { current: toastElement } = useContext(ToastContext);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    setButtonLoading(true);

    if (!stripe || !elements) {
      return;
      setButtonLoading(false);
    }

    const result = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
    });
    if (result.error) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          result.error.message ||
          "An error has occurred attempting to update card details",
      });
      setButtonLoading(false);
    } else {
      await updatePaymentMethod({
        orderId: orderInfo.id,
        paymentMethod: result.setupIntent.payment_method,
      })
        .unwrap()
        .then(() => {
          setButtonLoading(false);
          setShowUpdateSuccessFull(true);
        })
        .catch((error) => {
          setButtonLoading(false);
          toastElement?.show({
            severity: "error",
            summary: "Error",
            detail: `Message: ${error.message}`,
          });
        });
    }
  };

  return (
    <ContainerForm>
      <span className="title mb-4">Fill In New Card Details</span>
      <div className="sensitive">
        <PaymentElement
          onChange={(event) => setCardInformationComplete(event.complete)}
        />
      </div>
      <p className="text-sm gray mt-2 text-center font-normal">
        By clicking Update Card Details, you authorize Clear Health to
        automatically charge the credit card you have provided for the amount
        specified in your Payment Plan for each billing period.
      </p>
      <p className="text-sm gray mt-2 text-center font-normal mt-4">
        <i className="pi pi-lock mr-2 text-sm " />
        All transactions are securely protected through encryption
      </p>
      <Button
        type="submit"
        data-testid="button-submit-payment-plan"
        className="w-full justify-content-center mt-2  mb-1"
        onClick={handleSubmit}
        loading={buttonLoading}
        disabled={!cardInformationComplete}
      >
        <span className="font-bold">Update Card Details</span>
      </Button>
      <div className="flex align-items-center justify-content-center mt-3">
        <Button
          className="p-button-text mb-0 p-button-secondary p-0"
          onClick={onReturnClick}
        >
          <p className="text-sm purple font-semibold">Back to details</p>
        </Button>
      </div>
      <StyledInfoContainer className="flex w-full border-round-lg p-4 justify-content-center align-items-center text-sm gray mt-4 text-center font-normal">
        Thousands of patients have used Clear Health to reduce their medical
        bills and you can too.
      </StyledInfoContainer>
      <div className="relative">
        <img src="/logo_stripe.png" alt="stripe logo" />
      </div>
    </ContainerForm>
  );
}

export default function EditCardDetails({
  orderInfo,
  onReturnClick,
  setShowUpdateSuccessFull,
}: {
  orderInfo: IOrder;
  onReturnClick: () => void;
  setShowUpdateSuccessFull: (value: boolean) => void;
}) {
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    setStripePromise(loadStripe(getPublishableKey()));
  }, []);

  const [
    createOrderSetupIntent,
    { isError: isStripeError, isLoading: isStripeLoading },
  ] = useCreateOrderSetupIntentMutation();

  const [clientSecret, setClientSecret] = useState("");

  useEffect(() => {
    createOrderSetupIntent({ orderId: orderInfo.id })
      .unwrap()
      .then(({ data }: { data: any }) => setClientSecret(data.client_secret));
  }, []);

  if (isStripeError) {
    return <Navigate to="/not-found" />;
  }

  if (isStripeLoading || !clientSecret) {
    return <LoadingSpinner />;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        // HACK TO HIDE THE TERMS TEXT IN STRIPE EMBEDDED ELEMENT
        appearance: {
          rules: {
            ".TermsText": {
              color: "transparent",
              fontSize: "0px",
            },
          },
        },
      }}
    >
      <CardDetails
        orderInfo={orderInfo}
        onReturnClick={onReturnClick}
        setShowUpdateSuccessFull={setShowUpdateSuccessFull}
      />
    </Elements>
  );
}
